/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.stopWatchingForVisibility();
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        this.closest('.video-section').addEventListener('click', this.play.bind(this));
      }
    }

    /**
     * Whether this element actually generates a box on this breakpoint.
     * getClientRects() is empty when the element, or any ancestor, is display:none.
     * The background-video section renders BOTH a desktop and a mobile video into
     * the DOM and toggles them with CSS, so on mobile the desktop video is hidden
     * (and vice versa). Without this check the hidden video is still told to play,
     * which streams a full-size file the visitor can never see.
     * @returns {boolean}
     */
    isRendered() {
      return this.getClientRects().length > 0;
    }

    /**
     * Watches for the element becoming visible (e.g. on resize or orientation
     * change crossing the 1024px breakpoint) and initialises it at that point.
     */
    watchForVisibility() {
      if (this.visibilityWatcher) return;

      const check = () => {
        if (!this.isRendered()) return;
        this.stopWatchingForVisibility();
        this.initVideoTag();
      };

      this.visibilityWatcher = typeof window.debounce === 'function'
        ? window.debounce(check, 200)
        : check;

      window.addEventListener('resize', this.visibilityWatcher);
      window.addEventListener('orientationchange', this.visibilityWatcher);
    }

    stopWatchingForVisibility() {
      if (!this.visibilityWatcher) return;
      window.removeEventListener('resize', this.visibilityWatcher);
      window.removeEventListener('orientationchange', this.visibilityWatcher);
      this.visibilityWatcher = null;
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');
      if (!this.player) return;

      // Never start a video that isn't on screen for this breakpoint. Leave it
      // untouched (preload="none" means it costs nothing) and wait in case the
      // viewport changes.
      if (!this.isRendered()) {
        this.player.removeAttribute('autoplay');
        this.player.autoplay = false;
        this.watchForVisibility();
        return;
      }

      if (this.videoTagInitialised) return;
      this.videoTagInitialised = true;

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        this.closest('.video-section').classList.add('video-section--played');
      } else {
        this.player.addEventListener('playing', () => {
          this.closest('.video-section').classList.add('video-section--played');
        });

        this.addObserver(this.player);

        // Mute the video if a click didn't trigger its load
        const deferredMedia = this.closest('deferred-media');
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
        }

        this.play();
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.closest('.video-section').classList.add('video-section--played');
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
        return;
      }

      if (this.type === 'vimeo') {
        const vimeoPromise = this.player.play();
        if (vimeoPromise && typeof vimeoPromise.catch === 'function') vimeoPromise.catch(() => {});
        return;
      }

      // Don't start a video that isn't rendered at this breakpoint.
      if (!this.isRendered()) return;

      // play() returns a promise. If pause() is called before it settles the
      // browser rejects it with AbortError, and Safari can leave the video
      // stalled with no first frame painted. Track it so pause() can wait, and
      // swallow the rejection so it doesn't surface as an uncaught error.
      const promise = this.player.play();

      if (promise !== undefined) {
        this.playPromise = promise;

        const settle = () => {
          if (this.playPromise === promise) this.playPromise = null;
        };

        promise.then(settle).catch(settle);
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        const doPause = () => {
          // The element may have scrolled back into view while we were waiting
          // for the play promise to settle.
          if (this.inViewport || this.player.paused) return;
          this.player.pause();
          this.pausedByUser = false;
        };

        if (this.playPromise) {
          this.playPromise.then(doPause, doPause);
        } else {
          doPause();
        }
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        });
      }, { rootMargin: '0px 0px 200px 0px' });

      observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}